import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Ticket, Clock, CheckCircle2, XCircle, Ban, ScanSearch, X, FileText,
  ArrowLeft, Loader2, Send,
} from "lucide-react";
import { eventosPublicosApi, CompraEventoStatus } from "../../lib/api";
import { extraerDatosComprobante } from "../reservar/ocr-comprobante";
import { aplicarFaviconPagina, aplicarTituloPagina } from "../../lib/branding";

function qrUrl(texto: string, size = 240) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(texto)}&format=png&margin=10`;
}

const METODOS = ["Pago Móvil", "Zelle", "Transferencia bancaria", "PayPal", "Efectivo (depósito)", "Otro"];

export default function EventoCompraStatusPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<CompraEventoStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargar = () => {
    if (!id) return;
    eventosPublicosApi.status(Number(id)).then((d) => {
      setData(d);
      aplicarFaviconPagina(d.evento?.logoUrl);
      aplicarTituloPagina(d.evento?.sucursalNombre ?? d.evento?.titulo);
    }).catch((e) => setError(e?.message ?? "Compra no encontrada"));
  };

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error) {
    return (
      <div className="evc-wrap"><style>{STYLES}</style>
        <div className="evc-error">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="evc-wrap"><style>{STYLES}</style><div className="evc-loading">Cargando…</div></div>;
  }

  const fondoEstilo: CSSProperties = data.evento?.imagenFondoUrl
    ? {
        backgroundImage: `linear-gradient(rgba(246,241,231,0.88), rgba(246,241,231,0.88)), url(${data.evento.imagenFondoUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }
    : {};

  return (
    <div style={{ minHeight: "100vh", background: "#F6F1E7", ["--accent" as any]: data.evento?.colorPrimario || "#3A6B8C", ...fondoEstilo }}>
      <style>{STYLES}</style>
      <div className="evc-wrap">
        {data.evento && <Link to={`/eventos/${data.evento.id}`} className="evc-back"><ArrowLeft size={14} /> Volver al evento</Link>}

        <div className="evc-card">
          <div className="evc-head">
            <Ticket size={30} strokeWidth={1.6} />
            <h1>{data.evento?.titulo ?? `Compra #${data.id}`}</h1>
            {data.evento?.fechaEvento && (
              <p>{new Date(data.evento.fechaEvento.slice(0, 10) + "T00:00:00").toLocaleDateString("es-VE", { weekday: "long", day: "2-digit", month: "long" })}
                {data.evento.horaInicio ? ` · ${data.evento.horaInicio.slice(0, 5)}` : ""}</p>
            )}
          </div>

          <div className="evc-resumen">
            <span>{data.nombre}</span>
            <span>{data.cantidadEntradas} entrada(s) — USD {data.montoTotalUsd.toFixed(2)}</span>
          </div>

          {data.estado === "confirmada" && data.checkInToken && (
            <div className="evc-confirmada">
              <CheckCircle2 size={22} />
              <p><strong>Entrada confirmada.</strong> Mostrá este código QR en la puerta el día del evento.</p>
              <img src={qrUrl(data.checkInToken)} alt="QR de entrada" className="evc-qr" />
              {data.checkInAt && <p className="evc-usada">Ya se usó el {new Date(data.checkInAt).toLocaleString("es-VE")}</p>}
            </div>
          )}

          {data.estado === "pendiente_pago" && (
            <PagoPendiente compraId={data.id} data={data} onReportado={cargar} />
          )}

          {data.estado === "rechazada" && (
            <div className="evc-estado evc-estado-rose">
              <XCircle size={20} />
              <p>El último pago fue rechazado{data.ultimoPago?.motivoRechazo ? `: ${data.ultimoPago.motivoRechazo}` : "."}</p>
            </div>
          )}

          {data.estado === "expirada" && (
            <div className="evc-estado evc-estado-amber">
              <Clock size={20} />
              <p>El plazo para reportar el pago venció y se liberó el cupo. Si todavía te interesa, podés volver a comprar si quedan entradas.</p>
            </div>
          )}

          {data.estado === "cancelada" && (
            <div className="evc-estado evc-estado-rose">
              <Ban size={20} />
              <p>Esta compra fue cancelada.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PagoPendiente({ compraId, data, onReportado }: { compraId: number; data: CompraEventoStatus; onReportado: () => void }) {
  const [form, setForm] = useState({
    nombrePagador: data.nombre, telefono: "", email: "", montoUsd: String(data.montoTotalUsd),
    moneda: "USD", metodo: "Pago Móvil", banco: "", numeroReferencia: "",
    fechaPago: new Date().toISOString().slice(0, 10), notasCliente: "",
  });
  const [archivo, setArchivo] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [escaneando, setEscaneando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviado, setEnviado] = useState(false);

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  // Ya hay un pago reportado y en revisión -- no mostramos el formulario de nuevo.
  if (data.ultimoPago?.estado === "pendiente" || enviado) {
    return (
      <div className="evc-estado evc-estado-blue">
        <Clock size={20} />
        <p>Recibimos tu reporte de pago. Lo estamos verificando y te confirmamos tu entrada por WhatsApp apenas esté lista.</p>
      </div>
    );
  }

  const seleccionarArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { setError("La imagen no puede superar 10MB"); return; }
    setArchivo(f);
    setError(null);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target?.result as string);
      reader.readAsDataURL(f);
      setEscaneando(true);
      try {
        const datos = await extraerDatosComprobante(f);
        setForm((prev) => ({
          ...prev,
          ...(datos.referencia && !prev.numeroReferencia ? { numeroReferencia: datos.referencia } : {}),
          ...(datos.fecha ? { fechaPago: datos.fecha } : {}),
          ...(datos.banco && !prev.banco ? { banco: datos.banco } : {}),
          ...(datos.metodo ? { metodo: datos.metodo } : {}),
        }));
      } catch { /* OCR falla silenciosamente */ }
      setEscaneando(false);
    } else {
      setPreview(null);
    }
  };

  const enviar = async () => {
    if (!form.nombrePagador.trim()) { setError("Tu nombre es requerido"); return; }
    if (!form.montoUsd || Number(form.montoUsd) <= 0) { setError("Monto inválido"); return; }
    if (!form.numeroReferencia.trim()) { setError("Número de referencia requerido"); return; }
    setEnviando(true); setError(null);
    try {
      let comprobanteUrl: string | undefined;
      if (archivo) {
        const fd = new FormData();
        fd.append("file", archivo);
        const upRes = await fetch(`/api/v1/eventos-publicos/compras/${compraId}/upload-comprobante`, { method: "POST", body: fd });
        if (!upRes.ok) throw new Error("Error al subir el comprobante");
        comprobanteUrl = (await upRes.json()).url;
      }
      await eventosPublicosApi.reportarPago(compraId, {
        nombrePagador: form.nombrePagador.trim(),
        telefono: form.telefono.trim() || undefined,
        email: form.email.trim() || undefined,
        montoUsd: Number(form.montoUsd),
        moneda: form.moneda,
        metodo: form.metodo,
        banco: form.banco.trim() || undefined,
        numeroReferencia: form.numeroReferencia.trim(),
        fechaPago: form.fechaPago,
        notasCliente: form.notasCliente.trim() || undefined,
        comprobanteUrl,
      });
      setEnviado(true);
      onReportado();
    } catch (e: any) {
      setError(e?.message ?? "Error al reportar el pago");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="evc-pago">
      {data.minutosRestantes != null && (
        <div className="evc-countdown">
          <Clock size={16} /> Te quedan ~{Math.floor(data.minutosRestantes / 60)}h {data.minutosRestantes % 60}min para reportar el pago
        </div>
      )}
      {data.evento?.instruccionesPago && (
        <div className="evc-inst"><FileText size={13} /> {data.evento.instruccionesPago}</div>
      )}

      <div className="evc-grid">
        <Field label="Comprobante de pago (imagen)" full>
          <input ref={fileRef} type="file" accept="image/*,.pdf" onChange={seleccionarArchivo} className="evc-input" style={{ padding: "8px 10px" }} />
          {escaneando && <div className="evc-scanning"><ScanSearch size={15} className="inline animate-spin" /> Leyendo comprobante…</div>}
          {preview && (
            <div className="evc-preview">
              <img src={preview} alt="Vista previa" />
              <button type="button" onClick={() => { setArchivo(null); setPreview(null); if (fileRef.current) fileRef.current.value = ""; }} className="evc-preview-x"><X size={14} /></button>
            </div>
          )}
        </Field>
        <Field label="Tu nombre *"><input className="evc-input" value={form.nombrePagador} onChange={(e) => set("nombrePagador", e.target.value)} /></Field>
        <Field label="Teléfono"><input className="evc-input" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} /></Field>
        <Field label="Email"><input className="evc-input" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Monto pagado *">
          <div style={{ display: "flex", gap: 8 }}>
            <input className="evc-input" type="number" min="0" step="0.01" value={form.montoUsd} onChange={(e) => set("montoUsd", e.target.value)} style={{ flex: 1 }} />
            <select className="evc-input" value={form.moneda} onChange={(e) => set("moneda", e.target.value)} style={{ width: 90, flex: "none" }}>
              <option value="USD">USD</option><option value="Bs">Bs</option><option value="EUR">EUR</option>
            </select>
          </div>
        </Field>
        <Field label="Método de pago *">
          <select className="evc-input" value={form.metodo} onChange={(e) => set("metodo", e.target.value)}>
            {METODOS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="Banco"><input className="evc-input" value={form.banco} onChange={(e) => set("banco", e.target.value)} /></Field>
        <Field label="Número de referencia *"><input className="evc-input" value={form.numeroReferencia} onChange={(e) => set("numeroReferencia", e.target.value)} /></Field>
        <Field label="Fecha del pago" full><input className="evc-input" type="date" max={new Date().toISOString().slice(0, 10)} value={form.fechaPago} onChange={(e) => set("fechaPago", e.target.value)} /></Field>
      </div>

      {error && <div className="evc-form-error">{error}</div>}

      <button onClick={enviar} disabled={enviando} className="evc-btn">
        {enviando ? (<><Loader2 size={16} className="inline animate-spin" /> Enviando…</>) : (<><Send size={16} /> Reportar pago</>)}
      </button>
    </div>
  );
}

function Field({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return <div className={full ? "evc-field evc-field-full" : "evc-field"}><label>{label}</label>{children}</div>;
}

const STYLES = `
.evc-wrap { max-width: 640px; margin: 0 auto; padding: 20px 16px 48px; }
.evc-back { display: inline-flex; align-items: center; gap: 6px; color: var(--accent, #3A6B8C); font-size: 13px; text-decoration: none; margin-bottom: 14px; font-weight: 600; }
.evc-loading, .evc-error { text-align: center; color: #888; padding: 48px 16px; }
.evc-card { background: #fff; border-radius: 20px; padding: 24px; box-shadow: 0 4px 24px rgba(0,0,0,0.08); border: 1px solid #ece6d8; }
.evc-head { text-align: center; margin-bottom: 16px; color: var(--accent, #3A6B8C); }
.evc-head h1 { font-family: Georgia, serif; font-size: 20px; font-weight: 700; color: #1c1c1c; margin: 8px 0 4px; }
.evc-head p { font-size: 13px; color: #777; text-transform: capitalize; }
.evc-resumen { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 8px; background: #f6f1e7; border-radius: 12px; padding: 12px 16px; font-size: 13.5px; color: #444; margin-bottom: 18px; }
.evc-confirmada { text-align: center; color: #047857; }
.evc-confirmada p { font-size: 14px; color: #333; margin: 10px 0; }
.evc-qr { width: 200px; height: 200px; margin: 12px auto; display: block; border: 1px solid #e5e7eb; border-radius: 12px; padding: 8px; background: #fff; }
.evc-usada { font-size: 12px; color: #999 !important; }
.evc-estado { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 8px; padding: 18px; border-radius: 12px; }
.evc-estado p { font-size: 14px; margin: 0; }
.evc-estado-rose { background: #fee2e2; color: #991b1b; }
.evc-estado-amber { background: #fef3c7; color: #92400e; }
.evc-estado-blue { background: #eff6ff; color: #1d4ed8; }
.evc-pago { border-top: 1px solid #ece6d8; padding-top: 16px; }
.evc-countdown { display: inline-flex; align-items: center; gap: 6px; background: #fef3c7; color: #92400e; font-size: 13px; font-weight: 600; padding: 8px 12px; border-radius: 10px; margin-bottom: 12px; }
.evc-inst { background: #fff7ed; border: 1px solid #fed7aa; border-radius: 10px; padding: 10px; font-size: 13px; color: #9a3412; margin-bottom: 14px; display: flex; gap: 6px; align-items: flex-start; white-space: pre-wrap; }
.evc-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 14px; }
@media (max-width: 480px) { .evc-grid { grid-template-columns: 1fr; } }
.evc-field label { display: block; font-size: 11px; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }
.evc-field-full { grid-column: 1 / -1; }
.evc-input { width: 100%; padding: 11px 13px; border-radius: 10px; border: 1.5px solid #d1d5db; font-size: 16px; font-family: inherit; }
.evc-input:focus { outline: 2px solid rgba(58,107,140,0.35); border-color: var(--accent, #3A6B8C); }
.evc-form-error { background: #fee2e2; color: #991b1b; padding: 10px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.evc-scanning { display: inline-flex; align-items: center; gap: 6px; margin-top: 8px; padding: 8px 12px; border-radius: 8px; background: #eff6ff; color: #1d4ed8; font-size: 13px; font-weight: 500; }
.evc-preview { position: relative; display: inline-block; margin-top: 8px; }
.evc-preview img { max-width: 100%; max-height: 180px; border-radius: 10px; border: 1.5px solid #d1d5db; object-fit: contain; }
.evc-preview-x { position: absolute; top: -6px; right: -6px; width: 22px; height: 22px; border-radius: 50%; background: #ef4444; color: #fff; border: 2px solid #fff; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; padding: 0; }
.evc-btn { width: 100%; padding: 14px; background: linear-gradient(135deg, var(--accent, #3A6B8C), #2d5570); color: #fff; border-radius: 12px; font-weight: 700; font-size: 15px; border: none; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 4px 12px rgba(58,107,140,0.3); min-height: 48px; }
.evc-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.evc-btn .animate-spin { animation: evcSpin 0.9s linear infinite; }
@keyframes evcSpin { to { transform: rotate(360deg); } }
`;
